import {
  IDropdownResponse,
  INotesSectionData,
} from 'src/app/shared/models/dynamic.model';
import { EntityPermissionFields } from 'src/app/shared/models/permission-flags.model';

export interface Widget {
  id: number;
  title: string;
  value: string;
  extraClasses: string;
  iconName: string;
  name: string;
  controlName?: string;
}

export interface Icon {
  label: string;
  icon: string;
  event: string;
}

export interface DataSourceItem {
  shipmentsCreation: string;
  time: string;
  dispCode: string;
  icon: string;
}

export interface HawbCommodityElement {
  commodityNumber: number;
  hsCode: any;
  value: any;
  description: string;
  weight?: string | number;
  isInputRow?: boolean;
  id: number;
  hawbId: any;
}

export interface FieldOption {
  label: string;
  value: string;
}

export interface FormField {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  pattern?: string;
  minValue?: number;
  maxValue?: number;
  options?: FieldOption[];
  emailPatternError?: string;
  requiredError?: string;
  allowNumeric?: boolean;
  noInitialWhiteSpace?: boolean;
  allowAlphabet?: boolean;
  allowAlphaNumeric?: boolean;
  allowAlphabetWithSpace?: boolean;
  defaultValue?: string;
}

export interface FormConfig {
  fields: FormField[];
}

export interface ComponentMapping {
  title: string;
  componentType: any;
}
export interface ComponentSharedMapping {
  title: string;
  componentType: any;
  importurl: string;
}

export interface TableColumns {
  key: string;
  label: string;
  value: string;
  isSelected: boolean;
  defaultView: boolean;
  isEditable?: boolean;
  defaultPrintWidth?: number;
}

/* HAWB Summary Grid Result - Starts*/
export interface IHawbSummaryGridResult {
  statusCode: number;
  message: string;
  data: hawbSummaryGridData;
  code: any;
}

export interface IHawbInformationListResult {
  statusCode: number;
  message: string;
  data: hawbInformationListData;
}

export interface IOriginListResult {
  statusCode: number;
  message: string;
  data: Origin[];
}

export interface hawbSummaryGridData {
  totalRecords: number;
  sortingOrder: string;
  sortingField: string;
  hawb: hawbColumnData[];
}

export interface hawbInformationListData {
  totalRecords: number;
  sortingOrder: string;
  sortingField: string;
  hawb: hawbInformation[];
}

export interface HawbAfterGridListLoadedOptions {
  patchDetailHawbId?: number;
  loadDetailByHawbExt?: string;
  runFetchHawbInfo?: boolean;
  patchDetailRecordIndex?: number;
}

export type HawbListSearchCriteria =
  | {
      variant: 'basic';
      searchText: string;
      ids?: (string | number)[];
      isMasterChildRequired?: boolean;
    }
  | { variant: 'advance' };

export interface hawbColumnData {
  hawbId: number;
  hawbExt: string;
  shipper: string;
  consignee: string;
  noofPieces: any;
  noofShipment?: any;
  hawbValue: string;
  weight: any;
  shipmentWeight?: any;
  hawb_ext?: string;
  clearedCustoms?: any;
  bagged?: any;
  paid?: any;
  gatewayed?: any;
  cbpHeld?: string;
  clearanceChannel?: any;
  vendorReference?: string;
  masterShipmentId?: any;
  serviceType?: any;
  isParcelCarrier?: boolean;
  isParcialCareer?: boolean;
  isInvoiceSentGP?: boolean | string;
  isapinvoiceExists?: boolean | string;
  isAPInvoiceExist?: boolean | string;
  isApInvoiceExists?: boolean | string;
  checked?: boolean;
  tooltipInfo?: object;
  isRated?: string;
  isBlocked?: boolean | null;
  accountNumber?: number | string;
  customerId?: number;
  isSpecialService?: boolean | string;
  isAllowedToEdit?: EntityPermissionFields['isAllowedToEdit'];
  isSpainShipment?: EntityPermissionFields['isSpainShipment'];
  isShipmentHighTotalValue?: EntityPermissionFields['isShipmentHighTotalValue'];
  deliveryTerms?: string;
  isDomestic?: boolean | string;
  deliveryStatus?: string;
}
/* HAWB Summary Grid Result - Ends*/

/* HAWB Summary Delete Result - Starts */
export interface IHawbDeleteResult {
  statusCode: number;
  message: string;
  data: any;
  code: number;
  statusCodes: number;
  status?: number;
  body?: any;
}
/* HAWB Summary Delete Result - Ends */

export interface hawbInformation {
  hawbId: number;
  hawbExt: string;
  origin: string;
  finalDestination: string;
  accountNumber: string;
  customerId: number;
  customerName: number;
  serviceType: number;
  tag: string;
  description: string;
  commodity: Commodity;
  timeline: Timeline[];
  roles: Roles[];
  pod: Pod[];
  statusCodeList: DispList[];
  routes: object;
  commodityList: CommodityList[];
  pieces: PieceList[];
  attachmentList: Attachments[];
  miscellaneous: Miscellaneous;
  eventList: EventList[];
  hsCode: string;
  hawbValue: string;
  commodityDescription: string;
  totalNoOfShipments?: any;
  totalValue?: any;
  totalWeight?: any;
  hawbStatus: any;
  hawbCurrentStatus?: object;
  hawbPreviousStatus?: object;
  comments: string;
  originId?: string;
  destination?: string;
  notes: INotesSectionData;
  hawbStatusDate?: string;
  consigneeShipper: ConsigneeShipperStateModel;
  isGatewayed?: boolean;
  dimensionalWeight?: string;
  location?: string;
  selectedRoute?: any;
  /** false when shipment was created via fullman file or external webpage */
  isPaymentTermsEditable?: boolean;
}

export interface IHawbByIdResponse {
  statusCode: number;
  message: string;
  data: hawbInformation;
}

export interface Commodity {
  commodityNumber: string;
  hsCode: string;
  hawbValue: string;
  description: string;
}
export interface Timeline {
  dateTime: string;
  details: string;
}

export interface Roles {
  id?: number;
  roleType?: string;
  commodityId?: number;
  type?: string;
  name: string;
  email: string;
  phone: string;
  companyName: string;
  address: string;
  country: any;
  state: string;
  stateName?: string;
  city: string;
  zipCode: string;
  action?: string;
  countryCode?: string;
  address_1: string;
  isFromScreen?: string;
  /** API-loaded snapshot uses CrudActions.api; user-saved row edit uses CrudActions.ui (commodity roles). */
  source?: string;
}

export interface Pod {
  id: number;
  date: string;
  time: string;
  zone: string;
  agentName: string;
  comments: string;
  action: string;
  isInputRow?: boolean;
  info?: any;
}

export interface DispList {
  id?: number;
  date: string;
  time: string;
  zone?: string;
  codeDescription: string;
  note?: string;
  action?: string;
  isInputRow?: boolean;
  info?: any;
}

export interface CommodityList {
  id: number;
  commodityNumber: any;
  hsCode?: string;
  countryOfManufacturer?: string;
  description?: string;
  value?: any;
  hawbId?: number;
  url?: string;
  attachments?: [];
  miscellaneous?: CommodityMiscellaneous;
  isInputRow?: boolean;
  action?: string;
  commodity?: Record<string, never>;
  tempCommodityId?: number;
  notes?: INotesSectionData;
  roles?: [];
  eventList?: [];
  weight?: string | number;
}

export interface PieceList {
  dimH: number;
  dimL: number;
  dimW: number;
  hawbId: number;
  weight: number | string;
  isMaster: boolean;
  shipment: string;
  dimensions: string;
  statusDateTime: string;
  master_shipment: number | null;
  status: PieceStatus;
}

export interface PieceStatus {
  id: number;
  date: string;
  name: string;
  time: string;
  zone: string;
  action: string;
  userId: number;
  event_dt: string;
  obj_type: string;
  userName: string;
  eventType: string;
  formatedDateTime: string;
}

export interface Attachments {
  id?: string;
  type: string;
  fileName: string;
  signedUrl?: string;
  action?: string;
  isInputRow?: boolean;
  isFromScreen?: string;
  attachmentId?: string | null;
  isSavedData?: boolean;
}

export interface CommodityMiscellaneous {
  listPrice: string;
  orderDate: string;
  orderId: string;
  retailPrice: string;
  itemNumber: string;
  manufacturerId?: string;
  url: string;
  action: string;
  id: string;
  isSavedData?: boolean;
}

export interface Miscellaneous {
  isResidential: boolean;
  signatureRequired: boolean;
  originatorCode: string;
  ior?: string;
  deliveryTerms?: number | null;
}
export interface EventList {
  id: string;
  date: string;
  time: string;
  zone: string;
  name: string;
  info: any;
  eventType: any;
  apiEventType?: string;
  userName: any;
  description: any;
  timeZone: any;
  timestamp?: string;
  comments?: string;
  formatedDateTime?: string;
  isFromId?: any;
  requestType?: any;
}

export interface HawbInfoScreenStateModel {
  data: hawbInformation;
}

export interface IRolesData {
  roles: Roles[];
}

export const defaultMiscellaneous: Miscellaneous = {
  isResidential: false,
  signatureRequired: false,
  originatorCode: '',
  ior: '',
  deliveryTerms: null,
};

export const defaultNoteSection: INotesSectionData = {
  ibc: [],
  customer: [],
};

export const defaultCommodity: Commodity = {
  commodityNumber: '',
  hsCode: '',
  hawbValue: '',
  description: '',
};

export const defaultPiece: PieceList = {
  dimH: 0,
  dimL: 0,
  dimW: 0,
  hawbId: 0,
  weight: '',
  isMaster: false,
  shipment: '',
  dimensions: '',
  statusDateTime: '',
  master_shipment: 0,
  status: {
    id: 0,
    date: '',
    name: '',
    time: '',
    zone: '',
    action: '',
    userId: 0,
    event_dt: '',
    obj_type: '',
    userName: '',
    eventType: '',
    formatedDateTime: '',
  },
};

export const defaultConsigneeShipping: ConsigneeShipperStateModel = {
  consignee: {
    id: 1,
    name: '',
    email: '',
    phone: '',
    companyName: '',
    address_1: '',
    address_2: '',
    state: '',
    city: '',
    zipCode: '',
    countryCode: '',
    type: 'C',
  },
  shipper: {
    id: 1,
    name: '',
    email: '',
    phone: '',
    companyName: '',
    address_1: '',
    address_2: '',
    state: '',
    city: '',
    zipCode: '',
    countryCode: '',
    type: 'S',
  },
};

export const defaultHawbInformation: hawbInformation = {
  hawbId: 0,
  hawbExt: '',
  origin: '',
  finalDestination: '',
  accountNumber: '',
  customerId: 0,
  customerName: 0,
  serviceType: 0,
  tag: '',
  description: '',
  commodity: defaultCommodity,
  pieces: [defaultPiece],
  timeline: [],
  roles: [],
  notes: defaultNoteSection,
  pod: [],
  statusCodeList: [],
  routes: {},
  commodityList: [],
  attachmentList: [],
  miscellaneous: defaultMiscellaneous,
  eventList: [],
  hsCode: '',
  hawbValue: '',
  commodityDescription: '',
  hawbStatus: '',
  comments: '',
  hawbPreviousStatus: {},
  consigneeShipper: defaultConsigneeShipping,
  dimensionalWeight: '',
};

export interface CustomerInfo {
  id: number;
  name: string;
  number: string;
}

export const defaultCommodityMiscellaneous: CommodityMiscellaneous = {
  listPrice: '',
  orderDate: '',
  orderId: '',
  retailPrice: '',
  itemNumber: '',
  manufacturerId: '',
  url: '',
  action: 'add',
  id: '',
  isSavedData: false,
};

export const defaultAttachments: Attachments = {
  type: '',
  fileName: '',
};

export interface TransformedHawbData {
  commodity: {
    commodityNumber: any;
    hsCode: string;
    hawbValue: string;
    description: string;
  };
}

export interface TransformedCommodity {
  commodity: CommodityList;
  notes: INotesSectionData;
  attachments: Attachments;
  miscellaneous: CommodityMiscellaneous;
  fda: Record<string, unknown>;
  roles: any;
}

export interface HawbSearchPayload {
  hawbId?: string;
  hawb_ext?: string;
  originId?: number;
  destinationId?: number;
  customerId?: number;
  customerName?: string;
  accountNumber?: number;
  serviceType?: string;
  numPieces?: 0;
  hawbValue?: 0;
  totalWeight?: 0;
  searchType: string;
  data?: any;
  customer?: any;
  minHawbValue?: string;
  maxHawbValue?: string;
  minHawbWeight?: string;
  maxHawbWeight?: string;
  paging: {
    fromIndex: 0;
    toIndex: 0;
    sortingOrder: string;
    sortingField: string;
  };
}

export interface HawbInformationSearchPayload {
  hawbId?: number;
  hawbExt?: any;
  originId?: any;
  destinationId?: any;
  accountNumber?: any;
  customerId?: any;
  customerName?: any;
  serviceType?: any;
  numPieces?: any;
  hawbValue?: any;
  totalWeight?: any;
  searchType?: any;
  minHawbValue?: string;
  maxHawbValue?: string;
  minHawbWeight?: string;
  maxHawbWeight?: string;
  paging: {
    fromIndex: number;
    toIndex: number;
    sortingOrder?: string;
    sortingField?: string;
  };
}

export interface Origin {
  id: number;
  name: string;
}

export interface ConsigneeShippingModel {
  id?: number;
  type?: string;
  name: string;
  email: string;
  phone: string;
  companyName: string;
  address_1: string;
  address_2: string;
  state: string;
  stateName?: string;
  city: string;
  zipCode: string;
  action?: string;
  country?: string;
  countryCode: string;
  isFromScreen?: string;
}

export interface ConsigneeShipperStateModel {
  consignee: ConsigneeShippingModel;
  shipper: ConsigneeShippingModel;
}

export interface WidgetStateModel {
  totalWeight: string;
  totalValue: string;
  noOfPieces: string;
  totalNoOfShipments?: string;
  shipment: string;
  shipmentStatus?: string;
  location: string;
}

// Hawbs
export interface HawbDropdownList {
  origins: IDropdownResponse[];
  destinations: IDropdownResponse[];
  serviceType: IDropdownResponse[];
  attachmentType: IDropdownResponse[];
  agents: IDropdownResponse[];
  accounts: IDropdownResponse[];
  countries: IDropdownResponse[];
  statusCodes: IDropdownResponse[];
  roleTypes: IDropdownResponse[];
  facilities: IDropdownResponse[];
  shipmentTypes: IDropdownResponse[];
}

export interface ShipmentLabelAddressFormat {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  phone?: string;
  email?: string;
}
export interface ShipmentLabelGenerationModel {
  shipmentExt: string;
  shipperInfo:
    | {
        shipperName?: string;
        companyName?: string;
        account?: string;
        referenceNumber?: number;
        addressLine1: string;
        addressLine2?: string;
        city: string;
        state: string;
        postalCode: string;
        countryCode: string;
        phone?: string;
        email?: string;
      }
    | Record<string, unknown>;
  consigneeInfo: {
    consigneeName: string;
    companyName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    countryCode: string;
    phone?: string;
    email?: string;
  };
  body: {
    dateAndTime: string;
    value: string;
    weight: string;
    type: string;
    service: string;
    dimensions: string;
    description: string;
    masterShipment: string;
  };
}

export type HawbTransitPointAction = 'create' | 'update' | 'delete';

export interface HawbTransitPointPayload {
  action?: HawbTransitPointAction;
  hawbRouteTransitPointId?: number;
  sequenceOrder: number;
  originId: number;
  destinationId: number;
  modeOfTransport: number | null;
  modeOfTransportType: 'air' | 'land' | null;
  startingTime: string | null;
  arrivalTime: string | null;
  duration: number | null;
  minWeight: number | null;
  maxWeight: number | null;
  availability: number[] | null;
  charge: number;
}

export interface CreateHawbRoutePayload {
  hawbId: number;
  hawbRouteId?: number;
  transitPoints: HawbTransitPointPayload[];
}

export interface CreateHawbRouteApiResponse {
  statusCode: number;
  message: string;
  data: number;
}

export interface BulkRoutingValidateData {
  selectedCount: number;
  eligibleCount: number;
  eligibleHawbIds: number[];
  ineligibleCounts: {
    linked: number;
    held: number;
    blocked: number;
    notGatewayed: number;
    alreadyRouted: number;
    routingNotNeeded: number;
  };
  sharedOriginId: number;
  sharedDestinationId: number;
  availableRoutes: any[];
  promptMessage: string;
  mode: 'partial' | 'all';
}

export interface ApplyBulkRoutingPayload {
  hawbIds: number[];
  transitPoints: HawbTransitPointPayload[];
}

export interface ApplyBulkPodPayload {
  ids: number[];
  requestType: 'Shipment' | 'Manifest';
  dateTime: string;
  timeZoneId: number;
  driverName: string;
  carrierName: string;
}

/** Manifest POD (SPEC-BPM-001) — one eligible master shipment and its bag. */
export interface ManifestPodShipment {
  hawbId: number;
  hawbExt: string;
  bagId: number;
  bagExt: string;
}

/** `GET …/manifestPod/:manifestId/shipments` — eligible masters now + exclusion counts. */
export interface ManifestPodPreview {
  eligible: ManifestPodShipment[];
  heldCount: number;
  blockedCount: number;
  alreadyDeliveredCount: number;
  linkedCount: number;
}

/** `GET …/manifestPod/:statusCodeInfoId/linkedShipments` — stored at create time, never recomputed. */
export interface ManifestPodSnapshot {
  podDateTime: string | null;
  driverName: string;
  carrierName: string;
  shipments: ManifestPodShipment[];
}

export interface ManifestPodApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

export interface HawbRouteTransitPoint {
  hawbRouteTransitPointId: number;
  sequenceOrder: number;
  originId: number;
  destinationId: number;
  modeOfTransport: number;
  modeOfTransportDetail: { id: number; name: string };
  startingTime: string;
  arrivalTime: string;
  duration: number;
  minWeight: string;
  maxWeight: string;
  charge: string;
  availability: { availability: { id: number; name: string } }[];
  originFacility: { fac_id: number; name: string; facCode: string };
  destinationFacility: { fac_id: number; name: string; facCode: string };
}

export interface HawbRouteResponse {
  hawbRouteId: number;
  hawbId: number;
  originId: number;
  destinationId: number;
  totalDuration: number;
  totalCharge: string;
  transitPoints: HawbRouteTransitPoint[];
  originFacility: { fac_id: number; name: string; facCode: string };
  destinationFacility: { fac_id: number; name: string; facCode: string };
}

export interface FetchHawbRouteApiResponse {
  statusCode: number;
  message: string;
  data: HawbRouteResponse;
}

export interface FetchShipmentRoutesPayload {
  originId: number;
  destinationId: number;
  accountId: number | null;
  serviceType: string | null;
  shipmentValue: number | null;
  shipmentWeight: number | null;
  shipmentType: number;
  hawbId?: number;
}
